import { getApp } from "firebase/app";
import { type Auth, getAuth } from "firebase/auth";

/**
 * Recover the tenant slug when the app is opened with no tenant in the path —
 * notably the installed PWA, whose manifest `start_url` is "/" (it cannot be
 * baked per-tenant).
 *
 * This reads the tenant from Firebase Auth's PUBLIC, stable API: once the SDK
 * has restored its persisted session, `auth.currentUser.tenantId` carries the
 * user's Identity Platform `tenantId`, which in this app IS the tenant slug.
 *
 * Why this shape:
 * - Public/stable API only. The previous implementation scanned Firebase Auth's
 *   private IndexedDB store (`firebaseLocalStorageDb` / `firebaseLocalStorage`,
 *   `firebase:authUser:` keys), which is an SDK internal that breaks across SDK
 *   versions. `getAuth().authStateReady()` + `currentUser` is the supported path.
 * - No extra storage (apps/web rule #6): we never touch localStorage/
 *   sessionStorage; Firebase Auth owns its own persistence.
 * - Robust at PWA relaunch: Firebase Auth restores its session from its default
 *   persistence regardless of the launch URL, so this works even when the PWA
 *   starts at "/" with no slug in the path.
 */
export async function recoverTenantFromAuth(): Promise<string | null> {
  let auth: Auth;
  try {
    auth = getAuth(getApp("praesto"));
  } catch {
    return null; // Firebase app not initialized yet — nothing to recover.
  }
  // Wait once for the persisted session to be restored, but never hang the
  // PWA boot: race authStateReady() against a short timeout.
  const ready = auth.authStateReady().then(() => true);
  const timedOut = new Promise<false>((resolve) => {
    setTimeout(() => resolve(false), 2000);
  });
  await Promise.race([ready, timedOut]);
  // user.tenantId is the Identity Platform tenant, which in this app IS the slug.
  return auth.currentUser?.tenantId ?? null;
}
